import React, { useState, useEffect } from 'react';
import { objectiveAPI, achievementAPI, gamificationAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import GamificationProgress from './GamificationProgress';
import { Target, Award, Plus, Trash2, Zap, Pencil, Trophy, Star, Flame, Gem, HelpCircle, Users, TrendingUp, Info, ChevronRight, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { RichTextEditor, RichTextDisplay } from '../common/RichTextEditor';

const OBJECTIVE_COLORS = ['#2563EB', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16'];
const BADGE_COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4'];

// Achievement icons
const ACHIEVEMENT_ICONS = [
  { id: 'trophy', icon: Trophy, label: 'Trofeo' },
  { id: 'star', icon: Star, label: 'Estrella' },
  { id: 'target', icon: Target, label: 'Objetivo' },
  { id: 'flame', icon: Flame, label: 'Fuego' },
  { id: 'gem', icon: Gem, label: 'Diamante' },
];

const getIconComponent = (iconId) => {
  const found = ACHIEVEMENT_ICONS.find(i => i.id === iconId);
  return found ? found.icon : Trophy;
};

export default function GamificationTab({ classId, onRefresh, isProfessor = true }) {
  const [objectives, setObjectives] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('objectives');
  
  // Progress and level system
  const [progressSummary, setProgressSummary] = useState(null);
  const [levelSystem, setLevelSystem] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [refreshingProgress, setRefreshingProgress] = useState(false);
  
  // Objective form
  const [objectiveDialogOpen, setObjectiveDialogOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [objectiveForm, setObjectiveForm] = useState({
    name: '',
    description: '',
    icon: 'target',
    color: '#2563EB',
    max_xp: '',
    levels: []
  });

  // Achievement form
  const [achievementDialogOpen, setAchievementDialogOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [achievementForm, setAchievementForm] = useState({
    name: '',
    description: '',
    icon: 'trophy',
    xp_required: 50,
    learning_objective_id: '',
    use_multi_skill: false,
    skills_required: [],
    badge_color: '#F59E0B'
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [classId]);

  const loadData = async () => {
    try {
      const [objRes, achRes] = await Promise.all([
        objectiveAPI.list(classId),
        achievementAPI.list(classId)
      ]);
      setObjectives(objRes.data);
      setAchievements(achRes.data);
      
      // Load progress summary for professors
      if (isProfessor) {
        try {
          const [progressRes, levelRes] = await Promise.all([
            gamificationAPI.getProgressSummary(classId),
            gamificationAPI.getLevelSystem(classId)
          ]);
          setProgressSummary(progressRes.data);
          setLevelSystem(levelRes.data);
        } catch (e) {
          console.log('Progress summary not available');
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const refreshProgress = async () => {
    setRefreshingProgress(true);
    try {
      const [progressRes, levelRes] = await Promise.all([
        gamificationAPI.getProgressSummary(classId),
        gamificationAPI.getLevelSystem(classId)
      ]);
      setProgressSummary(progressRes.data);
      setLevelSystem(levelRes.data);
      toast.success('Progreso actualizado');
    } catch (error) {
      toast.error('Error al actualizar progreso');
    } finally {
      setRefreshingProgress(false);
    }
  };

  const resetObjectiveForm = () => {
    setObjectiveForm({ name: '', description: '', icon: 'target', color: '#2563EB', max_xp: '', levels: [] });
    setEditingObjective(null);
  };

  const resetAchievementForm = () => {
    setAchievementForm({
      name: '',
      description: '',
      icon: 'trophy',
      xp_required: 50,
      learning_objective_id: '',
      use_multi_skill: false,
      skills_required: [],
      badge_color: '#F59E0B'
    });
    setEditingAchievement(null);
  };

  const openEditObjective = (obj) => {
    setEditingObjective(obj);
    setObjectiveForm({
      name: obj.name,
      description: obj.description || '',
      icon: obj.icon || 'target',
      color: obj.color || '#2563EB',
      max_xp: obj.max_xp || '',
      levels: obj.levels || []
    });
    setObjectiveDialogOpen(true);
  };

  const openEditAchievement = (ach) => {
    setEditingAchievement(ach);
    const hasMultiSkill = ach.skills_required && ach.skills_required.length > 0;
    setAchievementForm({
      name: ach.name,
      description: ach.description || '',
      icon: ach.icon || 'trophy',
      xp_required: ach.xp_required || 0,
      learning_objective_id: ach.learning_objective_id || '',
      use_multi_skill: hasMultiSkill,
      skills_required: hasMultiSkill ? ach.skills_required : [],
      badge_color: ach.badge_color || '#F59E0B'
    });
    setAchievementDialogOpen(true);
  };

  const handleSaveObjective = async (e) => {
    e.preventDefault();
    if (!objectiveForm.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSubmitting(true);
    try {
      const submitData = {
        ...objectiveForm,
        max_xp: objectiveForm.max_xp ? parseInt(objectiveForm.max_xp) : null,
        levels: objectiveForm.levels.length > 0 
          ? objectiveForm.levels.map((l, i) => ({
              level: i + 1,
              name: l.name,
              xp_required: parseInt(l.xp_required) || 0
            })).sort((a, b) => a.xp_required - b.xp_required)
          : null
      };
      if (editingObjective) {
        await objectiveAPI.update(classId, editingObjective.id, submitData);
        toast.success('Habilidad actualizada');
      } else {
        await objectiveAPI.create(classId, submitData);
        toast.success('Habilidad creada');
      }
      setObjectiveDialogOpen(false);
      resetObjectiveForm();
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(editingObjective ? 'Error al actualizar' : 'Error al crear');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    if (!window.confirm('¿Eliminar esta habilidad? Las actividades asociadas perderán su referencia.')) return;
    try {
      await objectiveAPI.delete(classId, objectiveId);
      toast.success('Habilidad eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveAchievement = async (e) => {
    e.preventDefault();
    if (!achievementForm.name.trim() || !achievementForm.description || achievementForm.description === '<p><br></p>') {
      toast.error('Nombre y descripción son requeridos');
      return;
    }
    
    // Validate XP requirements
    if (achievementForm.use_multi_skill) {
      const totalXp = achievementForm.skills_required.reduce((sum, s) => sum + (s.xp_required || 0), 0);
      if (totalXp === 0) {
        toast.error('Debes asignar al menos 1 XP a alguna habilidad');
        return;
      }
    } else if (achievementForm.xp_required <= 0) {
      toast.error('El XP requerido debe ser mayor a 0');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        name: achievementForm.name,
        description: achievementForm.description,
        icon: achievementForm.icon,
        badge_color: achievementForm.badge_color,
        xp_required: achievementForm.use_multi_skill ? 0 : achievementForm.xp_required,
        learning_objective_id: achievementForm.use_multi_skill ? null : (achievementForm.learning_objective_id || null),
        skills_required: achievementForm.use_multi_skill 
          ? achievementForm.skills_required.filter(s => s.xp_required > 0)
          : null
      };
      
      if (editingAchievement) {
        await achievementAPI.update(classId, editingAchievement.id, payload);
        toast.success('Logro actualizado');
      } else {
        await achievementAPI.create(classId, payload);
        toast.success('Logro creado');
      }
      setAchievementDialogOpen(false);
      resetAchievementForm();
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(editingAchievement ? 'Error al actualizar' : 'Error al crear');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAchievement = async (achievementId) => {
    if (!window.confirm('¿Eliminar este logro?')) return;
    try {
      await achievementAPI.delete(classId, achievementId);
      toast.success('Logro eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-heading font-bold text-slate-900">Sistema de Gamificación</h3>
        <p className="text-slate-500">Configura objetivos de aprendizaje y logros para motivar a tus estudiantes</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="objectives">
            <Target className="w-4 h-4 mr-2" />
            Habilidades
          </TabsTrigger>
          <TabsTrigger value="achievements">
            <Award className="w-4 h-4 mr-2" />
            Logros
          </TabsTrigger>
          {isProfessor && (
            <TabsTrigger value="progress">
              <TrendingUp className="w-4 h-4 mr-2" />
              Progreso
            </TabsTrigger>
          )}
          <TabsTrigger value="guide">
            <BookOpen className="w-4 h-4 mr-2" />
            Guía
          </TabsTrigger>
        </TabsList>

        {/* Learning Objectives Tab */}
        <TabsContent value="objectives" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Define las habilidades o competencias que quieres desarrollar. Las actividades otorgan XP en estas habilidades.
            </p>
            <Dialog open={objectiveDialogOpen} onOpenChange={(open) => {
              setObjectiveDialogOpen(open);
              if (!open) resetObjectiveForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-objective-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Habilidad
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleSaveObjective}>
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingObjective ? 'Editar Habilidad' : 'Nueva Habilidad'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingObjective ? 'Modifica los datos de la habilidad' : 'Define un objetivo de aprendizaje para tus estudiantes'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input
                        placeholder="Ej: Pensamiento Crítico"
                        value={objectiveForm.name}
                        onChange={(e) => setObjectiveForm({...objectiveForm, name: e.target.value})}
                        required
                        data-testid="objective-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <RichTextEditor
                        value={objectiveForm.description}
                        onChange={(val) => setObjectiveForm({...objectiveForm, description: val})}
                        placeholder="Describe esta habilidad..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        {OBJECTIVE_COLORS.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              objectiveForm.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setObjectiveForm({...objectiveForm, color})}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Levels System */}
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-semibold">Sistema de Niveles</Label>
                          <p className="text-xs text-slate-500">Define metas de XP para distintos niveles de dominio</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs">XP Máximo de la habilidad</Label>
                        <Input
                          type="number"
                          placeholder="Ej: 600"
                          value={objectiveForm.max_xp}
                          onChange={(e) => setObjectiveForm({...objectiveForm, max_xp: e.target.value})}
                          min="0"
                          data-testid="objective-max-xp"
                        />
                      </div>
                      
                      {objectiveForm.levels.map((level, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          <Badge variant="outline" className="h-6 w-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </Badge>
                          <Input
                            placeholder="Nombre del nivel"
                            value={level.name}
                            onChange={(e) => {
                              const updated = [...objectiveForm.levels];
                              updated[idx] = { ...updated[idx], name: e.target.value };
                              setObjectiveForm({...objectiveForm, levels: updated});
                            }}
                            className="h-8 text-sm flex-1"
                            data-testid={`level-name-${idx}`}
                          />
                          <Input
                            type="number"
                            placeholder="XP"
                            value={level.xp_required}
                            onChange={(e) => {
                              const updated = [...objectiveForm.levels];
                              updated[idx] = { ...updated[idx], xp_required: e.target.value };
                              setObjectiveForm({...objectiveForm, levels: updated});
                            }}
                            className="h-8 text-sm w-20"
                            min="0"
                            data-testid={`level-xp-${idx}`}
                          />
                          <span className="text-xs text-slate-400 flex-shrink-0">XP</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 flex-shrink-0"
                            onClick={() => {
                              const updated = objectiveForm.levels.filter((_, i) => i !== idx);
                              setObjectiveForm({...objectiveForm, levels: updated});
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          const nextNum = objectiveForm.levels.length + 1;
                          setObjectiveForm({
                            ...objectiveForm,
                            levels: [...objectiveForm.levels, { name: '', xp_required: '' }]
                          });
                        }}
                        data-testid="add-level-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Agregar Nivel
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} data-testid="submit-objective">
                      {submitting ? 'Guardando...' : (editingObjective ? 'Guardar Cambios' : 'Crear')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {objectives.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay habilidades definidas</p>
                <p className="text-sm text-slate-400">Crea habilidades para que las actividades otorguen XP específico</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {objectives.map(obj => (
                <Card key={obj.id} className="border-slate-200 overflow-hidden" data-testid={`objective-${obj.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: obj.color + '20' }}
                        >
                          <Target className="w-5 h-5" style={{ color: obj.color }} />
                        </div>
                        <div>
                          <CardTitle className="font-heading text-lg">{obj.name}</CardTitle>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => openEditObjective(obj)}
                          data-testid={`edit-objective-${obj.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteObjective(obj.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {obj.description && (
                    <CardContent className="pt-0 overflow-hidden">
                      <div className="text-sm text-slate-500"><RichTextDisplay content={obj.description} /></div>
                    </CardContent>
                  )}
                  {(obj.levels && obj.levels.length > 0) && (
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{obj.levels.length} niveles</span>
                          {obj.max_xp && <span>Max: {obj.max_xp} XP</span>}
                        </div>
                        {/* Level progress bar */}
                        <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                          {obj.levels.sort((a, b) => a.xp_required - b.xp_required).map((lvl, i) => {
                            const pct = obj.max_xp ? (lvl.xp_required / obj.max_xp) * 100 : 0;
                            return (
                              <div
                                key={i}
                                className="absolute top-0 h-full border-r-2 border-white"
                                style={{
                                  left: 0,
                                  width: `${pct}%`,
                                  backgroundColor: obj.color + (40 + i * 20).toString(16),
                                  zIndex: obj.levels.length - i
                                }}
                              />
                            );
                          })}
                        </div>
                        {/* Level labels */}
                        <div className="flex flex-wrap gap-1">
                          {obj.levels.sort((a, b) => a.xp_required - b.xp_required).map((lvl, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] py-0 h-5" style={{ borderColor: obj.color, color: obj.color }}>
                              Nv.{lvl.level} {lvl.name} ({lvl.xp_required}XP)
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Los logros se desbloquean automáticamente cuando los estudiantes alcanzan cierta cantidad de XP.
            </p>
            <Dialog open={achievementDialogOpen} onOpenChange={(open) => {
              setAchievementDialogOpen(open);
              if (!open) resetAchievementForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-amber-500 hover:bg-amber-600" data-testid="create-achievement-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Logro
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <form onSubmit={handleSaveAchievement}>
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingAchievement ? 'Editar Logro' : 'Nuevo Logro'}
                    </DialogTitle>
                    <DialogDescription>
                      {editingAchievement ? 'Modifica los datos del logro' : 'Define un logro que se desbloqueará al alcanzar cierto XP'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label>Nombre del Logro</Label>
                      <Input
                        placeholder="Ej: Maestro del Pensamiento"
                        value={achievementForm.name}
                        onChange={(e) => setAchievementForm({...achievementForm, name: e.target.value})}
                        required
                        data-testid="achievement-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <RichTextEditor
                        value={achievementForm.description}
                        onChange={(val) => setAchievementForm({...achievementForm, description: val})}
                        placeholder="Ej: Alcanzaste 100 puntos en Pensamiento Crítico"
                      />
                    </div>
                    
                    {/* Icon Selector */}
                    <div className="space-y-2">
                      <Label>Icono</Label>
                      <div className="flex gap-2">
                        {ACHIEVEMENT_ICONS.map(iconItem => {
                          const IconComp = iconItem.icon;
                          return (
                            <button
                              key={iconItem.id}
                              type="button"
                              title={iconItem.label}
                              className={`w-12 h-12 rounded-lg flex items-center justify-center border-2 transition-all ${
                                achievementForm.icon === iconItem.id 
                                  ? 'border-amber-500 bg-amber-50 scale-105' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              onClick={() => setAchievementForm({...achievementForm, icon: iconItem.id})}
                            >
                              <IconComp className={`w-6 h-6 ${achievementForm.icon === iconItem.id ? 'text-amber-600' : 'text-slate-500'}`} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Multi-skill toggle */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="font-medium">Logro Multi-Habilidad</Label>
                        <p className="text-xs text-slate-500">Requiere XP en múltiples habilidades</p>
                      </div>
                      <Switch
                        checked={achievementForm.use_multi_skill}
                        onCheckedChange={(checked) => {
                          setAchievementForm({
                            ...achievementForm,
                            use_multi_skill: checked,
                            skills_required: checked && objectives.length > 0
                              ? objectives.map(o => ({ skill_id: o.id, skill_name: o.name, xp_required: 0 }))
                              : []
                          });
                        }}
                      />
                    </div>
                    
                    {/* Single skill XP */}
                    {!achievementForm.use_multi_skill && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>XP Requerido</Label>
                          <div className="relative">
                            <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500" />
                            <Input
                              type="number"
                              min="1"
                              value={achievementForm.xp_required}
                              onChange={(e) => setAchievementForm({...achievementForm, xp_required: parseInt(e.target.value) || 0})}
                              className="pl-9"
                              required
                              data-testid="achievement-xp"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Habilidad (opcional)</Label>
                          <Select 
                            value={achievementForm.learning_objective_id || "_none"} 
                            onValueChange={(v) => setAchievementForm({...achievementForm, learning_objective_id: v === "_none" ? "" : v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="XP Total" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">XP Total (todas)</SelectItem>
                              {objectives.map(obj => (
                                <SelectItem key={obj.id} value={obj.id}>{obj.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    
                    {/* Multi-skill XP requirements */}
                    {achievementForm.use_multi_skill && objectives.length > 0 && (
                      <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <Label className="text-purple-700 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          XP Requerido por Habilidad
                        </Label>
                        <div className="space-y-2">
                          {objectives.map(obj => {
                            const currentReq = achievementForm.skills_required.find(s => s.skill_id === obj.id)?.xp_required || 0;
                            return (
                              <div key={obj.id} className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: obj.color }} />
                                  <span className="text-sm text-slate-700">{obj.name}</span>
                                </div>
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 h-8"
                                  value={currentReq}
                                  onChange={(e) => {
                                    const newVal = parseInt(e.target.value) || 0;
                                    setAchievementForm({
                                      ...achievementForm,
                                      skills_required: achievementForm.skills_required.map(s =>
                                        s.skill_id === obj.id 
                                          ? { ...s, xp_required: newVal }
                                          : s
                                      )
                                    });
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-purple-600 mt-2">
                          Total: {achievementForm.skills_required.reduce((sum, s) => sum + (s.xp_required || 0), 0)} XP
                        </p>
                      </div>
                    )}
                    
                    {achievementForm.use_multi_skill && objectives.length === 0 && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                        Primero crea habilidades para usar logros multi-habilidad.
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Color del Badge</Label>
                      <div className="flex gap-2">
                        {BADGE_COLORS.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              achievementForm.badge_color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setAchievementForm({...achievementForm, badge_color: color})}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} data-testid="submit-achievement">
                      {submitting ? 'Guardando...' : (editingAchievement ? 'Guardar Cambios' : 'Crear')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {achievements.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Award className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay logros definidos</p>
                <p className="text-sm text-slate-400">Crea logros para motivar a tus estudiantes</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {achievements.map(ach => {
                const IconComponent = getIconComponent(ach.icon);
                const hasMultiSkill = ach.skills_required && ach.skills_required.length > 0;
                
                return (
                  <Card key={ach.id} className="border-slate-200 overflow-hidden" data-testid={`achievement-${ach.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: ach.badge_color + '20' }}
                          >
                            <IconComponent className="w-6 h-6" style={{ color: ach.badge_color }} />
                          </div>
                          <div>
                            <CardTitle className="font-heading text-lg">{ach.name}</CardTitle>
                            {hasMultiSkill ? (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {ach.skills_required.map((skill, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs bg-purple-50/50">
                                    {skill.skill_name}: {skill.xp_required}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-purple-600 font-medium flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  {ach.xp_required} XP
                                </span>
                                {ach.learning_objective_name && (
                                  <span className="text-xs text-slate-500">
                                    en {ach.learning_objective_name}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                            onClick={() => openEditAchievement(ach)}
                            data-testid={`edit-achievement-${ach.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteAchievement(ach.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 overflow-hidden">
                      <div className="text-sm text-slate-500"><RichTextDisplay content={ach.description} /></div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Progress Tab - Professor View */}
        {isProfessor && (
          <TabsContent value="progress" className="mt-4">
            <GamificationProgress
              progressSummary={progressSummary}
              refreshing={refreshingProgress}
              onRefresh={refreshProgress}
            />
          </TabsContent>
        )}

        {/* Guide Tab - Interactive Explanation */}
        <TabsContent value="guide" className="space-y-6 mt-4">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <BookOpen className="w-5 h-5" />
                ¿Cómo Funciona el Sistema de Gamificación?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Levels Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-bold text-lg flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-purple-600" />
                  Sistema de Niveles
                </h4>
                <p className="text-slate-600 mb-4">
                  Los estudiantes suben de nivel acumulando XP (puntos de experiencia). 
                  Cada nivel requiere más XP que el anterior.
                </p>
                
                {levelSystem && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Niveles disponibles:</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 max-h-48 overflow-y-auto">
                      {levelSystem.levels.slice(0, 10).map(lvl => (
                        <div 
                          key={lvl.level}
                          className="p-2 bg-purple-50 rounded text-center border border-purple-100"
                        >
                          <p className="font-bold text-purple-700">Nivel {lvl.level}</p>
                          <p className="text-xs text-purple-600">{lvl.title}</p>
                          <p className="text-xs text-slate-500">{lvl.xp_min} XP</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Fórmula: {levelSystem.formula}
                    </p>
                  </div>
                )}
              </div>

              {/* Skills Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-bold text-lg flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-blue-600" />
                  Habilidades (Skills)
                </h4>
                <p className="text-slate-600 mb-4">
                  Las habilidades son competencias específicas que quieres desarrollar en tus estudiantes. 
                  Cada actividad puede otorgar XP en una o más habilidades.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-blue-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Crea habilidades</strong> que representen las competencias del curso
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-blue-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Asigna XP</strong> a las actividades para cada habilidad
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-blue-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      Los estudiantes <strong>ven su progreso</strong> en cada habilidad
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-blue-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      El XP acumulado <strong>desbloquea logros</strong> automáticamente
                    </p>
                  </div>
                </div>
              </div>

              {/* Achievements Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-bold text-lg flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-yellow-600" />
                  Logros (Achievements)
                </h4>
                <p className="text-slate-600 mb-4">
                  Los logros son reconocimientos que los estudiantes desbloquean al alcanzar ciertos 
                  niveles de XP en una o más habilidades.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-yellow-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Logro simple:</strong> Requiere X XP en una habilidad específica
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-yellow-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Logro multi-habilidad:</strong> Requiere XP en varias habilidades
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-yellow-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      Se <strong>desbloquean automáticamente</strong> al cumplir requisitos
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-yellow-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      Aparecen en el <strong>perfil del estudiante</strong> como insignias
                    </p>
                  </div>
                </div>
              </div>

              {/* Coins Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-bold text-lg flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="bold">$</text>
                  </svg>
                  Coins (Monedas)
                </h4>
                <p className="text-slate-600 mb-4">
                  Los Coins son la moneda virtual del sistema. Los estudiantes los ganan 
                  completando actividades y pueden gastarlos en el Marketplace.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Ganar:</strong> Completando actividades asignadas
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 mt-1" />
                    <p className="text-sm text-slate-600">
                      <strong>Gastar:</strong> En productos del Marketplace
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
